import React, { useState } from 'react';
import { Mail, ChevronDown, Apple, Play, Trash2, RefreshCw, CreditCard } from 'lucide-react';
import Footer from '../components/Footer';
import { LegalNav } from '../components/LegalShell';

const FAQ = [
  {
    q: 'How do I cancel my subscription?',
    a: (
      <>
        <p className="mb-2">Subscriptions are managed by Apple or Google. We can&apos;t cancel them on your behalf.</p>
        <p className="font-semibold text-white/80 mt-3 mb-1 flex items-center gap-2"><Apple className="w-4 h-4" /> iOS</p>
        <p className="text-sm">Open <em>Settings &gt; [your name] &gt; Subscriptions &gt; ResetProtocol &gt; Cancel Subscription</em>.</p>
        <p className="font-semibold text-white/80 mt-3 mb-1 flex items-center gap-2"><Play className="w-4 h-4" /> Android</p>
        <p className="text-sm">Open <em>Google Play &gt; Profile &gt; Payments &amp; subscriptions &gt; Subscriptions &gt; ResetProtocol &gt; Cancel</em>.</p>
        <p className="text-sm mt-3 text-white/40">Cancellation takes effect at the end of the current billing period — you keep access until then.</p>
      </>
    ),
  },
  {
    q: 'How do I restore my purchase on a new device?',
    a: (
      <>
        <p>Open the app → <em>Settings → Restore Purchases</em>, then sign in with the same Apple ID or Google account you used to purchase. RevenueCat will re-link your subscription instantly.</p>
        <p className="text-sm mt-2 text-white/40">If it doesn&apos;t restore within 60 seconds, force-close the app and try again, or email us.</p>
      </>
    ),
  },
  {
    q: 'How do I delete my account and all my data?',
    a: (
      <>
        <p className="mb-2">You have two options:</p>
        <ol className="list-decimal pl-6 space-y-1 text-sm">
          <li>In the app: <em>Settings → Delete Account → Confirm permanent deletion</em>. This is immediate and irreversible.</li>
          <li>By email: send a deletion request from your registered email to <a href="mailto:support@resetprotocol.app" className="text-[#a83a4a] hover:underline">support@resetprotocol.app</a>. We process within 30 days.</li>
        </ol>
        <p className="mt-3 text-sm text-white/60">When deleted: personal account info is permanently removed, recovery data and analytics are erased, and only limited records required for fraud-prevention or payment compliance may be retained.</p>
        <p className="text-sm mt-2 text-white/40">Deleting your account does <strong>not</strong> cancel your store subscription — please cancel that separately in your device settings (see above).</p>
      </>
    ),
  },
  {
    q: 'I was charged but my account isn\'t Pro.',
    a: (
      <>
        <p>Tap <em>Settings → Restore Purchases</em> first. If that doesn&apos;t fix it, email <a href="mailto:support@resetprotocol.app" className="text-[#a83a4a] hover:underline">support@resetprotocol.app</a> with your transaction ID (in your Apple/Google receipt) and we&apos;ll resolve it within 24 hours.</p>
      </>
    ),
  },
  {
    q: 'Can I get a refund?',
    a: (
      <>
        <p>Refunds for App Store purchases are handled by Apple at <a href="https://reportaproblem.apple.com" target="_blank" rel="noreferrer" className="text-[#a83a4a] hover:underline">reportaproblem.apple.com</a>. Refunds for Google Play are handled at <a href="https://play.google.com" target="_blank" rel="noreferrer" className="text-[#a83a4a] hover:underline">play.google.com</a> &gt; Order History. We do not have the ability to issue store-side refunds directly.</p>
      </>
    ),
  },
  {
    q: 'I\'m not getting notifications.',
    a: (
      <>
        <p>Check that notifications are enabled in your device settings under ResetProtocol, and that <em>In-app Settings → Notifications</em> are toggled on. If they still don&apos;t arrive, sign out and back in to refresh your push token.</p>
      </>
    ),
  },
  {
    q: 'How does the Content Shield work?',
    a: (
      <>
        <p>The Content Shield uses an optional CleanBrowsing DNS profile installed on your device for explicit-content filtering. The app independently verifies that the DNS is actually filtering before showing as Active — the status cannot be faked. Removing the profile (Settings → VPN &amp; Device Management) will deactivate the shield instantly.</p>
      </>
    ),
  },
  {
    q: 'How does the AI Coach work?',
    a: (
      <>
        <p>Messages you send are processed by our LLM provider to generate a reply. A short rolling history per user is kept to maintain context. We do not train third-party models on your conversations and you can clear your history at any time in Settings.</p>
      </>
    ),
  },
];

function FaqItem({ q, a, idx, open, onToggle }) {
  return (
    <div className="border border-white/[0.06] bg-white/[0.015]">
      <button
        onClick={() => onToggle(idx)}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-white/[0.02] transition"
        data-testid={`support-faq-${idx}`}
      >
        <span className="font-heading text-sm md:text-base text-white pr-4 tracking-tight">{q}</span>
        <ChevronDown className={`w-4 h-4 text-white/40 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-out ${open ? 'max-h-[600px]' : 'max-h-0'}`}>
        <div className="px-5 pb-5 text-white/60 font-body text-sm leading-relaxed space-y-2">{a}</div>
      </div>
    </div>
  );
}

function QuickCard({ icon, title, desc, ctaLabel, ctaHref, ctaTestid }) {
  const isInternal = ctaHref?.startsWith('#');
  return (
    <div className="p-6 border border-white/[0.06] bg-white/[0.015] flex flex-col">
      <div className="w-9 h-9 flex items-center justify-center mb-4 text-[#a83a4a]">{icon}</div>
      <h3 className="font-heading text-base text-white mb-2 tracking-tight">{title}</h3>
      <p className="text-white/45 text-sm font-body leading-relaxed mb-5 flex-1">{desc}</p>
      <a
        href={ctaHref}
        {...(isInternal ? {} : { target: '_blank', rel: 'noreferrer' })}
        className="inline-flex items-center gap-2 text-[#a83a4a] hover:text-[#c44a5a] font-body text-xs uppercase tracking-[0.15em] self-start transition"
        data-testid={ctaTestid}
      >
        {ctaLabel} →
      </a>
    </div>
  );
}

export default function Support() {
  const [open, setOpen] = useState(0);

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      <LegalNav />
      <main className="container mx-auto max-w-4xl px-6 py-12 md:py-16">
        {/* Hero */}
        <header className="mb-14 pb-10 border-b border-white/5" data-testid="support-hero">
          <p className="text-[#a83a4a] text-[10px] uppercase tracking-[0.3em] font-body mb-3">Support</p>
          <h1 className="font-heading text-4xl md:text-5xl text-white mb-4 tracking-tight">How can we help?</h1>
          <p className="text-white/50 font-body text-base max-w-2xl leading-relaxed mb-6">
            Most issues are resolved in seconds with the steps below. If you still need a human, email us — we reply within one business day.
          </p>
          <a
            href="mailto:support@resetprotocol.app"
            className="inline-flex items-center gap-2 px-5 py-3 bg-white text-black hover:bg-white/90 font-heading text-sm uppercase tracking-[0.15em] transition"
            data-testid="support-email-cta"
          >
            <Mail className="w-4 h-4" />
            support@resetprotocol.app
          </a>
        </header>

        {/* Quick links */}
        <section className="mb-14">
          <h2 className="font-heading text-xl text-white mb-6 tracking-tight">Common requests</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <QuickCard
              icon={<CreditCard className="w-5 h-5" strokeWidth={1.5} />}
              title="Cancel subscription"
              desc="Manage and cancel your ResetProtocol subscription in your device settings."
              ctaLabel="See instructions"
              ctaHref="#cancel-sub"
              ctaTestid="quick-cancel"
            />
            <QuickCard
              icon={<RefreshCw className="w-5 h-5" strokeWidth={1.5} />}
              title="Restore purchase"
              desc="Switching devices or just upgraded? Restore your active subscription in one tap."
              ctaLabel="How to restore"
              ctaHref="#restore"
              ctaTestid="quick-restore"
            />
            <QuickCard
              icon={<Trash2 className="w-5 h-5" strokeWidth={1.5} />}
              title="Delete account"
              desc="Permanently erase your account and all associated data within 30 days."
              ctaLabel="Delete my data"
              ctaHref="#delete"
              ctaTestid="quick-delete"
            />
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-14" id="cancel-sub">
          <h2 className="font-heading text-xl text-white mb-6 tracking-tight">Frequently asked</h2>
          <div className="space-y-2" data-testid="support-faq-list">
            {FAQ.map((item, i) => (
              <FaqItem
                key={item.q}
                q={item.q}
                a={item.a}
                idx={i}
                open={open === i}
                onToggle={(idx) => setOpen(open === idx ? null : idx)}
              />
            ))}
          </div>
        </section>

        {/* Anchor sections for direct links */}
        <section id="restore" className="sr-only">Restore Purchase — see FAQ above.</section>
        <section id="delete" className="sr-only">Delete Account — see FAQ above.</section>

        {/* Email block */}
        <section className="border border-[#7a1f2b]/30 bg-gradient-to-br from-[#3a0e15]/30 to-transparent p-8 md:p-10 text-center">
          <h2 className="font-heading text-2xl text-white mb-3 tracking-tight">Still stuck?</h2>
          <p className="text-white/50 font-body text-sm mb-6 max-w-md mx-auto leading-relaxed">
            Send us your account email and a short description. We&apos;ll get back to you within one business day.
          </p>
          <a
            href="mailto:support@resetprotocol.app"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black hover:bg-white/90 font-heading text-sm uppercase tracking-[0.15em] transition"
            data-testid="support-email-bottom"
          >
            <Mail className="w-4 h-4" />
            Email Support
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
}
